import { inject } from '@angular/core';
import {
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpEvent,
  HttpRequest,
} from '@angular/common/http';
import {
  catchError,
  switchMap,
  throwError,
  retry,
  Observable,
  MonoTypeOperatorFunction,
} from 'rxjs';
import { AuthService } from '../../features/auth/services/auth.service';
import { AuthResponse } from '../models/auth.reponse';
import { environment } from '../../../environments/environment';

const addAuthHeader = (req: HttpRequest<unknown>, token: string) =>
  req.clone({
    headers: req.headers.set('Authorization-Yonewi', `Bearer ${token}`),
  });

export const authInterceptorFn: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);

  if (req.url.includes('/token') || req.url.includes('/refreshtoken')) {
    return next(req);
  }

  if (environment.skipAuthToken) {
    return next(req);
  }

  const authToken = auth.getToken();

  if (!authToken) {
    return next(req);
  }

  const handleUnauthorized = (
    requestToRetry = req,
  ): MonoTypeOperatorFunction<HttpEvent<unknown>> =>
    catchError((error: HttpErrorResponse): Observable<HttpEvent<unknown>> => {
      if (error.status === 401 && !req.url.includes('/token')) {
        const refreshToken = auth.getRefreshToken();
        if (refreshToken) {
          return auth.refreshToken().pipe(
            retry(3),
            switchMap((response: AuthResponse) =>
              next(addAuthHeader(requestToRetry, response.access_token)),
            ),
            catchError((refreshError) => {
              auth.logout();
              return throwError(() => refreshError);
            }),
          );
        }
      }
      return throwError(() => error);
    });

  if (auth.shouldRefreshAccessToken()) {
    const refreshToken = auth.getRefreshToken();
    if (refreshToken) {
      return auth.refreshToken().pipe(
        retry(3),
        switchMap((response: AuthResponse) =>
          next(addAuthHeader(req, response.access_token)).pipe(
            handleUnauthorized(req),
          ),
        ),
        catchError((refreshError) => {
          auth.logout();
          return throwError(() => refreshError);
        }),
      );
    }
  }

  const authReq = addAuthHeader(req, authToken);

  return next(authReq).pipe(handleUnauthorized(req));
};
